import type { FC } from 'react';
import { Box } from '../Box';
import { Stack } from '../Stack';
import type { CardProps } from '../Card';
import { Card } from '../Card';
import { Typography } from '../Typography';
import { Link } from '../Link';

export type DevelopmentPlaceholderProps = {
  /**
   * A custom label to describe the component this placeholder represents.
   *
   * If not set, "Coming soon" will still be displayed unless `hideLabel` is `true`.
   */
  label?: string;
  /**
   * A Jira issue key to link to.
   *
   * e.g. "NJS-544"
   */
  issue?: string;
  /**
   * Abstract size in multiples of 6rem.
   *
   * If not set, size will be determined automatically.
   */
  size?: number;
  /**
   * If `true`, does not display any label (neither custom nor default).
   */
  hideLabel?: boolean;
  /**
   * A teaser text to display either as label (if no custom label is provided) or as a teaser above the custom label
   *
   * @default 'Coming soon'
   */
  teaser?: string;
} & Pick<CardProps, 'sx' | 'children'>;

/**
 * A placeholder for components that are not yet available, for use in layouts
 * */
export const DevelopmentPlaceholder: FC<DevelopmentPlaceholderProps> = ({
  label,
  issue,
  size,
  hideLabel,
  teaser = 'Coming soon',
  sx,
  children,
}) => (
  <Card
    sx={{
      padding: 2,
      backgroundImage:
        'linear-gradient(135deg, #f5cb3f 25%, #474747 25%, #474747 50%, #f5cb3f 50%, #f5cb3f 75%, #474747 75%, #474747 100%)',
      backgroundSize: '56.57px 56.57px',
      height: size ? `${size * 6}rem` : undefined,
      display: 'flex',
      alignItems: 'flex-start',
      ...sx,
    }}
  >
    {!hideLabel && (
      <Box sx={{ padding: 1, borderRadius: 0.5, backgroundColor: '#f5cb3f' }}>
        {(label || issue) && (
          <Typography variant="body3" sx={{ margin: 0, textTransform: 'uppercase' }}>
            {teaser}
          </Typography>
        )}
        <Stack direction="row" flexWrap="wrap">
          {issue && (
            <Link
              sx={{ color: 'text.light', marginRight: 0.5 }}
              href={`https://empiriecom.atlassian.net/browse/${issue}`}
              target="_blank"
            >
              <Typography variant="h3" sx={{ margin: 0, whiteSpace: 'nowrap' }}>
                {issue}
              </Typography>
            </Link>
          )}
          <Typography variant="h3" sx={{ margin: 0, display: 'flex', flexWrap: 'wrap' }}>
            {label || issue ? label : teaser}
          </Typography>
        </Stack>
      </Box>
    )}
    {children}
  </Card>
);
